/**
 * Parts: the solids one object (or one group of primitives) contributes, with
 * what "sits on" needs: its up axis, its body without the knobs on its top,
 * and its knobs (studs, side studs, pins) as connectors along frame axes. Plain numbers, no Babylon.
 */

import { FACE_STRIDE, moveSolid, type FitSolid, type Vec3 } from './solids';

export interface FitPart {
    readonly solids: readonly FitSolid[];
    readonly lo: Vec3;
    readonly hi: Vec3;
    /** The part's own up (its local +Y) in the frame, unit length. */
    readonly up: Vec3;
    /** The box without the knobs on its top: what a part resting on it sits on. The full box when it has none. */
    readonly body: { readonly lo: Vec3; readonly hi: Vec3 };
    /** The height of the knobs (LEGO studs) on its top, or null when it has none. */
    readonly knob: number | null;
    /**
     * The solids that stand out of a face as knobs, on any side: studs on its top, side studs, axle pins.
     * Each points out along a frame axis. None when the knobs are welded to the body.
     */
    readonly knobs: readonly FitKnob[];
}

/** A solid that is a knob: it stands `height` out of the part's face that looks along `sign` times frame axis `axis`. */
export interface FitKnob {
    readonly solid: FitSolid;
    readonly axis: 0 | 1 | 2;
    readonly sign: 1 | -1;
    readonly height: number;
}

/** The longest side of a box. */
export const sizeOf = (lo: Vec3, hi: Vec3): number => Math.max(hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]);

/**
 * A part from its placed solids and its up direction. Knobs are found per side from the faces that look
 * out of it: an outermost level that covers at most 60% of that side's footprint, standing on a level of the
 * body below it that covers at least a quarter of it, is knobs on a body. That holds whether the knobs are
 * separate pieces or welded to the body; only separate pieces are listed in `knobs`, with every piece the size
 * of a top stud that stands on a lower face. The body is cut below the knobs on its up side only, so "sits on"
 * means resting on the body top with the studs engaged.
 */
export function fitPart(solids: readonly FitSolid[], up: Vec3): FitPart {
    const lo: Vec3 = [Infinity, Infinity, Infinity], hi: Vec3 = [-Infinity, -Infinity, -Infinity];
    for (const s of solids) for (let k = 0; k < 3; k++) { lo[k] = Math.min(lo[k], s.lo[k]); hi[k] = Math.max(hi[k], s.hi[k]); }
    const len = Math.hypot(up[0], up[1], up[2]) || 1;
    const u: Vec3 = [up[0] / len, up[1] / len, up[2] / len];
    const upAxis = [0, 1, 2].reduce((best, k) => (Math.abs(u[k]) > Math.abs(u[best]) ? k : best), 0) as 0 | 1 | 2;
    const upSign = Math.abs(u[upAxis]) >= 0.99 ? Math.sign(u[upAxis]) : 0;
    const body = { lo: [...lo] as Vec3, hi: [...hi] as Vec3 };
    const knobs: FitKnob[] = [];
    let knob: number | null = null;
    for (const axis of [0, 1, 2] as const) {
        for (const sign of [1, -1] as const) {
            const found = knobsAlong(solids, axis, sign, lo, hi);
            if (!found) continue;
            const { top, height } = found;
            if (axis === upAxis && sign === upSign) {
                knob = height;
                if (sign > 0) body.hi[axis] = top; else body.lo[axis] = -top;
            }
            // A knob piece is narrower than the face it stands on and stands out of it: it starts at the face
            // (a stud, a pin's collar) or ends at the knob tops, maybe running through the body (an axle).
            const [i, j] = [0, 1, 2].filter(k => k !== axis);
            for (const s of solids) {
                const from = sign > 0 ? s.lo[axis] : -s.hi[axis], to = sign > 0 ? s.hi[axis] : -s.lo[axis];
                const narrow = (s.hi[i] - s.lo[i]) * (s.hi[j] - s.lo[j]) <= 0.6 * (hi[i] - lo[i]) * (hi[j] - lo[j]);
                const out = to <= top + height * 1.01 && (from >= top - height / 2 || to >= top + height / 2);
                if (narrow && out && to > top) knobs.push({ solid: s, axis, sign, height });
            }
            // Studs repeat: a piece the size of a stud on its top, standing on a lower face of a wider piece (under a
            // raised end), is one too.
            const seen = knobs.filter(k => k.axis === axis && k.sign === sign);
            if (axis !== upAxis || sign !== upSign || seen.length < 2) continue;
            const eps = (hi[axis] - lo[axis]) * 1e-3;
            for (const s of solids) {
                if (!seen.some(k => k.solid === s) && seen.some(k => sameBox(k.solid, s)) && standsOn(solids, s, axis, sign, eps)) knobs.push({ solid: s, axis, sign, height });
            }
        }
    }
    return { solids, lo, hi, up: u, body, knob, knobs };
}

/** Whether `s` stands on a wider piece of the part: its end toward -`sign` along `axis` lies in a face of that piece looking `sign`. */
function standsOn(solids: readonly FitSolid[], s: FitSolid, axis: 0 | 1 | 2, sign: 1 | -1, eps: number): boolean {
    const [i, j] = [0, 1, 2].filter(k => k !== axis);
    const across = (x: FitSolid): number => (x.hi[i] - x.lo[i]) * (x.hi[j] - x.lo[j]);
    const at = sign > 0 ? s.lo[axis] : -s.hi[axis];
    return solids.some(t => {
        if (t === s || across(t) <= across(s) || !(t.lo[i] < s.hi[i] && t.hi[i] > s.lo[i] && t.lo[j] < s.hi[j] && t.hi[j] > s.lo[j])) return false;
        for (let o = 0; o < t.faces.length; o += FACE_STRIDE) if (t.faces[o + axis] * sign >= 0.9999 && Math.abs(t.faces[o + 3] - at) <= eps) return true;
        return false;
    });
}

const sameBox = (a: FitSolid, b: FitSolid): boolean => [0, 1, 2].every(k => Math.abs((a.hi[k] - a.lo[k]) - (b.hi[k] - b.lo[k])) <= 0.02 * (a.hi[k] - a.lo[k]) + 1e-9);

function knobsAlong(solids: readonly FitSolid[], axis: 0 | 1 | 2, sign: 1 | -1, lo: Vec3, hi: Vec3): { top: number; height: number } | null {
    const others = [0, 1, 2].filter(k => k !== axis);
    const footprint = (hi[others[0]] - lo[others[0]]) * (hi[others[1]] - lo[others[1]]);
    const height = hi[axis] - lo[axis];
    if (!(footprint > 0) || !(height > 0)) return null;
    const eps = height * 1e-3;
    // Area of outward faces per level (the level is the face plane's offset along the axis), and the solids there.
    const levels: { at: number; area: number; solids: Set<FitSolid> }[] = [];
    for (const s of solids) {
        const f = s.faces;
        for (let o = 0; o < f.length; o += FACE_STRIDE) {
            if (f[o + axis] * sign < 0.9999) continue;
            const at = f[o + 3];
            let level = levels.find(l => Math.abs(l.at - at) <= eps);
            if (!level) levels.push(level = { at, area: 0, solids: new Set() });
            level.area += f[o + 4];
            level.solids.add(s);
        }
    }
    levels.sort((a, b) => b.at - a.at);
    const top = levels[0];
    if (!top || top.area > 0.6 * footprint || top.area < 0.02 * footprint) return null;
    // A level is the body's unless every solid there is a stud-sized piece standing on a wider one (a stud under a
    // cap): at most 4 times the cross-section of a piece on top. A block standing on a plate is body.
    const [i, j] = others;
    const across = (s: FitSolid): number => (s.hi[i] - s.lo[i]) * (s.hi[j] - s.lo[j]);
    const piece = Math.max(...[...top.solids].map(across));
    const body = (l: { solids: Set<FitSolid> }): boolean => [...l.solids].some(s => across(s) > 4 * piece || !standsOn(solids, s, axis, sign, eps));
    const base = levels.find(l => l.at < top.at - eps && l.area >= 0.25 * footprint && body(l));
    if (!base || top.at - base.at > 0.5 * height) return null;
    return { top: base.at, height: top.at - base.at };
}

/** The studs of a stud-built model: their height, their width and the grid they stand on (0 when welded to their bodies). */
export interface StudGrid {
    readonly height: number;
    readonly width: number;
    readonly pitch: number;
}

/** The modal value of `values` (within `tol` of it, relative), and how many share it. Sorted, so n log n. */
function modal(values: readonly number[], tol: number): { value: number; count: number } {
    const sorted = [...values].sort((x, y) => x - y);
    let value = sorted[0] ?? 0, count = 0;
    for (let i = 0, lo = 0, hi = 0; i < sorted.length; i++) {
        const v = sorted[i];
        while (sorted[lo] < v - tol * v) lo++;
        while (hi < sorted.length && sorted[hi] <= v + tol * v) hi++;
        if (hi - lo > count) { value = v; count = hi - lo; }
    }
    return { value, count };
}

/**
 * The studs of a model built from studded bricks, else null. Studs are up knobs of one height, short and
 * round, standing on one square grid whose pitch is under twice their width (LEGO: 1.7 mm tall, 4.8 mm wide,
 * 8 mm apart), on at least three parts and a fifth of them. Studs welded to their bodies can't be measured:
 * then half the parts must carry knobs of one height. Claws, pins, bolt heads and caster stems are knobs
 * too, but they are tall, flat or off any grid, or too few.
 */
export function studGrid(parts: readonly FitPart[]): StudGrid | null {
    const knobbed = parts.filter(p => p.knob !== null);
    const height = modal(knobbed.map(p => p.knob as number), 0.01);
    if (height.count < 3) return null;
    const best = height.value;
    const studded = knobbed.filter(p => Math.abs((p.knob as number) - best) <= 0.01 * best);
    const upKnobs = (p: FitPart) => p.knobs.filter(k => Math.abs(k.height - best) <= 0.01 * best && Math.abs(p.up[k.axis]) >= 0.99 && Math.sign(p.up[k.axis]) === k.sign);
    if (studded.filter(p => !upKnobs(p).length).length >= parts.length / 2) return { height: best, width: 0, pitch: 0 };
    // Each part's round, short up knobs at that height: their width and their centres across the up axis.
    const pieces = studded.map(p => upKnobs(p).flatMap(k => {
        const [i, j] = [0, 1, 2].filter(a => a !== k.axis);
        const wi = k.solid.hi[i] - k.solid.lo[i], wj = k.solid.hi[j] - k.solid.lo[j];
        if (Math.min(wi, wj) < 0.9 * Math.max(wi, wj) || best > 0.75 * Math.max(wi, wj)) return [];
        return [{ width: Math.max(wi, wj), at: [(k.solid.lo[i] + k.solid.hi[i]) / 2, (k.solid.lo[j] + k.solid.hi[j]) / 2] }];
    }));
    const width = modal(pieces.flat().map(s => s.width), 0.03).value;
    // A part's pitch: the nearest two of its studs of that width; its studs lie on that grid.
    const pitches = pieces.flatMap(list => {
        const studs = list.filter(s => Math.abs(s.width - width) <= 0.03 * width);
        if (studs.length < 2) return [];
        let pitch = Infinity;
        for (let a = 0; a < studs.length; a++) for (let b = a + 1; b < studs.length; b++) pitch = Math.min(pitch, Math.hypot(studs[a].at[0] - studs[b].at[0], studs[a].at[1] - studs[b].at[1]));
        const onGrid = studs.every(s => s.at.every((v, k) => { const n = (v - studs[0].at[k]) / pitch; return Math.abs(n - Math.round(n)) <= 0.05; }));
        return onGrid && pitch > width && pitch < 2 * width ? [pitch] : [];
    });
    const pitch = modal(pitches, 0.03);
    return pitch.count >= 3 && pitch.count >= parts.length / 5 ? { height: best, width, pitch: pitch.value } : null;
}

/** The same part moved by `d`. */
export function movePart(part: FitPart, d: Vec3): FitPart {
    const add = (v: Vec3): Vec3 => [v[0] + d[0], v[1] + d[1], v[2] + d[2]];
    const solids = part.solids.map(s => moveSolid(s, d));
    const knobs = part.knobs.map(k => ({ ...k, solid: solids[part.solids.indexOf(k.solid)] }));
    return { ...part, solids, knobs, lo: add(part.lo), hi: add(part.hi), body: { lo: add(part.body.lo), hi: add(part.body.hi) } };
}

/**
 * What is under a part, along `down` (a unit frame axis): the part whose body top is highest at or below the
 * part's body bottom within its footprint, with the drop that seats it there, and the parts in its footprint
 * that reach above its bottom (they block a straight drop). Boxes only: a slope or a curve under the part
 * reads as its box, so the drop is a hint, not a sweep of the meshes.
 */
export function supportUnder<T>(part: FitPart, others: ReadonlyMap<T, FitPart>, down: Vec3, tol: number): { under: T | null; drop: number; blocking: T[] } {
    const axis = [0, 1, 2].reduce((best, k) => (Math.abs(down[k]) > Math.abs(down[best]) ? k : best), 0);
    const sign = -Math.sign(down[axis]);
    const across = [0, 1, 2].filter(k => k !== axis);
    const bottom = sign > 0 ? part.body.lo[axis] : -part.body.hi[axis];
    let under: T | null = null, best = -Infinity;
    const blocking: T[] = [];
    for (const [key, other] of others) {
        if (other === part) continue;
        if (!across.every(k => other.body.lo[k] < part.body.hi[k] - tol && other.body.hi[k] > part.body.lo[k] + tol)) continue;
        const top = sign > 0 ? other.body.hi[axis] : -other.body.lo[axis];
        const otherBottom = sign > 0 ? other.body.lo[axis] : -other.body.hi[axis];
        if (otherBottom >= bottom) continue;
        if (top <= bottom + tol) { if (top > best) { best = top; under = key; } }
        else blocking.push(key);
    }
    return { under, drop: under === null ? 0 : bottom - best, blocking };
}
